import React, { useState, useRef } from "react";
import {
  Col,
  Row,
  OverlayTrigger,
  Tooltip,
  Button,
  Form,
  Card,
  Badge,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  faEnvelope,
  faPhone,
  faArrowUp,
  faCircleDollarToSlot,
  faRedo,
  faFileCsv,
  faUserGraduate,
  faCalendarAlt,
  faChalkboardTeacher,
  faCopy,
  faExternalLinkAlt,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notify } from "react-notify-toast";
import moment from "moment";
import PaymentHistorial from "../details/modals/PaymentHistorial";
import ExportCsvModal from "./ExportCsvModal";
import CountryFlag from "./CountryFlag";
import { getStudentLocation } from "../../../helpers/getStudentLocation";

const LOCATION_GUESS_NOTE = "* Basado en numero de telefono";

export default function ListDisplay({
  studentsList,
  studentCount,
  refreshList,
  lastUpdate,
  setPageSize,
  pageSize,
  currentQuery,
}) {
  const [copyValue, setCopyValue] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const defaultPhonePrefix = "54";
  const defaultWspGreetings = "Buenos días ";
  const SCROLL_TO_TOP_THRESHOLD = 11;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const inputRef = useRef();

  const copyToClipboard = (textToCopy) => {
    if (!textToCopy) return;
    setCopyValue(textToCopy);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(textToCopy);
    } else {
      if (inputRef.current) {
        inputRef.current.select();
        document.execCommand("copy");
      }
    }
    notify.show("Copiado al portapapeles", "info");
  };

  const getWspUrl = (phoneNumber, studentName) => {
    if (!phoneNumber) return "#";
    const cleanedPhone = phoneNumber.replace("+", "").replace(/\s/g, "");
    const phoneWithPrefix =
      cleanedPhone.substr(0, 2) === defaultPhonePrefix
        ? cleanedPhone
        : `${defaultPhonePrefix}${cleanedPhone}`;
    return `https://wa.me/${phoneWithPrefix}?text=${encodeURIComponent(
      defaultWspGreetings + studentName
    )}`;
  };

  const renderStatusBadge = (student) => {
    const isArray = Array.isArray(student?.isActive);
    if (!isArray) {
      return (
        <Badge variant="secondary" className="px-2 py-1">
          Otro ⚫
        </Badge>
      );
    }
    const status = student?.isActive[student?.isActive.length - 1];
    switch (status) {
      case "up":
        return (
          <Badge variant="success" className="px-2 py-1 font-weight-normal">
            Activo 🟢
          </Badge>
        );
      case "down":
        return (
          <Badge variant="danger" className="px-2 py-1 font-weight-normal">
            Baja 🔴
          </Badge>
        );
      case "commissionChanged":
        return (
          <Badge variant="warning" className="px-2 py-1 font-weight-normal text-dark">
            Cambio comisión 🟡
          </Badge>
        );
      case "posibleDown":
        return (
          <Badge variant="warning" className="px-2 py-1 font-weight-normal text-dark">
            Posible baja ⚠️
          </Badge>
        );
      case "done":
        return (
          <Badge variant="info" className="px-2 py-1 font-weight-normal">
            Finalizó 🎓
          </Badge>
        );
      case "prepay":
        return (
          <Badge variant="primary" className="px-2 py-1 font-weight-normal">
            Seña 💵
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="px-2 py-1 font-weight-normal">
            Otro ⚫
          </Badge>
        );
    }
  };

  const getInitials = (name = "", surname = "") => {
    const firstInitial = name.trim() ? name.trim()[0].toUpperCase() : "E";
    const lastInitial = surname.trim() ? surname.trim()[0].toUpperCase() : "";
    return `${firstInitial}${lastInitial}`;
  };

  const openPaymentModal = (studentId) => {
    setSelectedStudentId(studentId);
    setShowPaymentModal(true);
  };

  return (
    <Col className="p-0 my-3">
      {/* Header bar */}
      <Card className="border-0 shadow-sm mb-3 bg-white">
        <Card.Body className="p-3 d-flex flex-wrap align-items-center justify-content-between">
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <div className="bg-primary text-white rounded p-2 mr-3 d-flex align-items-center justify-content-center" style={{ width: "38px", height: "38px" }}>
              <FontAwesomeIcon icon={faUserGraduate} />
            </div>
            <div>
              <span className="font-weight-bold text-dark d-block">
                Listado de Estudiantes
              </span>
              <small className="text-muted">
                Total de registros: <strong className="text-primary">{studentCount}</strong>
              </small>
            </div>
          </div>

          <div className="d-flex align-items-center" style={{ gap: "12px" }}>
            <div className="d-flex align-items-center">
              <small className="text-secondary mr-2 font-weight-bold">Filas:</small>
              <Form.Control
                as="select"
                size="sm"
                value={pageSize}
                onChange={(event) => setPageSize(parseInt(event.target.value))}
                className="bg-light border-secondary"
                style={{ width: "80px" }}
              >
                {[10, 20, 30, 40, 50, 100].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Form.Control>
            </div>

            {lastUpdate && (
              <small className="text-muted d-none d-lg-inline">
                Actualizado: {lastUpdate}
              </small>
            )}

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="export-csv-tooltip">Exportar CSV</Tooltip>}
            >
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => setShowExportModal(true)}
                className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                style={{ width: "34px", height: "34px" }}
              >
                <FontAwesomeIcon icon={faFileCsv} />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="refresh-tooltip">Refrescar listado completo</Tooltip>}
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={refreshList}
                className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                style={{ width: "34px", height: "34px" }}
              >
                <FontAwesomeIcon icon={faRedo} />
              </Button>
            </OverlayTrigger>
          </div>
        </Card.Body>
      </Card>

      {/* Student List */}
      {Array.isArray(studentsList) && studentsList.length === 0 ? (
        <Card className="border-0 shadow-sm text-center py-5 bg-white">
          <Card.Body>
            <div className="text-muted mb-3">
              <FontAwesomeIcon icon={faUserGraduate} size="3x" className="opacity-50" />
            </div>
            <h6 className="text-dark font-weight-bold">No se encontraron estudiantes</h6>
            <p className="small text-muted mb-0">
              Prueba modificando o limpiando los filtros de búsqueda.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <div className="d-flex flex-column" style={{ gap: "10px" }}>
          {studentsList.map((student) => {
            const initials = getInitials(student.name, student.surname);
            const studentLocation = getStudentLocation(student);
            const latestClassroom =
              Array.isArray(student.classroom) && student.classroom.length > 0
                ? student.classroom[student.classroom.length - 1]
                : typeof student.classroom === "string"
                  ? student.classroom
                  : null;

            return (
              <Card
                key={student._id}
                className="border-0 shadow-sm bg-white hover-shadow transition-all"
                style={{ borderRadius: "10px" }}
              >
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    {/* Avatar & Name Info */}
                    <Col lg={4} md={5} className="mb-2 mb-md-0">
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle bg-light border text-primary font-weight-bold d-flex align-items-center justify-content-center mr-3 flex-shrink-0"
                          style={{
                            width: "44px",
                            height: "44px",
                            fontSize: "1.05rem",
                            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)",
                          }}
                        >
                          {initials}
                        </div>
                        <div>
                          <div className="d-flex align-items-center">
                            <Link
                              to={`/students/details/${student._id}`}
                              className="font-weight-bold text-dark text-decoration-none h6 mb-0 mr-2"
                            >
                              {student.name} {student.surname}
                            </Link>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tt-detail-${student._id}`}>Ver ficha alumno</Tooltip>}
                            >
                              <Link
                                to={`/students/details/${student._id}`}
                                className="text-muted small"
                              >
                                <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                              </Link>
                            </OverlayTrigger>
                          </div>

                          <div className="d-flex align-items-center flex-wrap mt-1" style={{ gap: "6px" }}>
                            {renderStatusBadge(student)}
                            {Array.isArray(student.classroom) && student.classroom.length > 1 && (
                              <Badge variant="light" className="border text-primary font-weight-normal px-2 py-1">
                                {student.classroom.length} comisiones
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </Col>

                    {/* Academic info */}
                    <Col lg={3} md={3} className="mb-2 mb-md-0">
                      <div className="small text-secondary">
                        <div className="d-flex align-items-center mb-1">
                          <FontAwesomeIcon icon={faChalkboardTeacher} className="mr-2 text-primary" />
                          <span>
                            {latestClassroom ? (
                              <strong className="text-dark">{latestClassroom}</strong>
                            ) : (
                              <span className="text-muted italic">Sin comisión ⚠</span>
                            )}
                          </span>
                        </div>
                        {student.createdAt && (
                          <div className="d-flex align-items-center text-muted" style={{ fontSize: "0.8rem" }}>
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 opacity-75" />
                            Alta: {moment(student.createdAt).format("DD/MM/YYYY")}
                          </div>
                        )}
                      </div>
                    </Col>

                    {/* Contact Info */}
                    <Col lg={3} md={4} className="mb-2 mb-md-0">
                      <div className="small">
                        {student.phone ? (
                          <div className="d-flex align-items-center mb-1">
                            <FontAwesomeIcon icon={faPhone} className="mr-2 text-muted" />
                            <span className="text-dark font-weight-medium mr-2">{student.phone}</span>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tt-copy-phone-${student._id}`}>Copiar teléfono</Tooltip>}
                            >
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted mr-2"
                                onClick={() => copyToClipboard(student.phone)}
                              >
                                <FontAwesomeIcon icon={faCopy} size="xs" />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tt-wsp-${student._id}`}>Enviar WhatsApp</Tooltip>}
                            >
                              <a
                                href={getWspUrl(student.phone, student.name)}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-outline-success rounded-circle p-0 d-inline-flex align-items-center justify-content-center"
                                style={{ width: "24px", height: "24px" }}
                              >
                                <FontAwesomeIcon icon={faWhatsapp} size="xs" />
                              </a>
                            </OverlayTrigger>
                          </div>
                        ) : (
                          <div className="text-muted italic mb-1">Sin teléfono</div>
                        )}

                        {student.email ? (
                          <div className="d-flex align-items-center text-truncate">
                            <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-muted" />
                            <span className="text-dark text-truncate mr-2" style={{ maxWidth: "150px" }} title={student.email}>
                              {student.email}
                            </span>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`tt-copy-${student._id}`}>Copiar email</Tooltip>}
                            >
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted"
                                onClick={() => copyToClipboard(student.email)}
                              >
                                <FontAwesomeIcon icon={faCopy} size="xs" />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        ) : (
                          <div className="text-muted italic">Sin correo</div>
                        )}

                        <div className="text-muted">
                          <CountryFlag country={studentLocation.country?.split(" *")[0]} />
                          País: {studentLocation.country || "-"}
                        </div>
                        <div className="text-muted">
                          Provincia: {studentLocation.region || "-"}
                        </div>
                        {studentLocation.isGuessed && (
                          <small className="text-danger d-block">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                            {LOCATION_GUESS_NOTE}
                          </small>
                        )}
                      </div>
                    </Col>

                    {/* Actions */}
                    <Col lg={2} md={12} className="text-md-right mt-2 mt-lg-0">
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip id={`tt-pay-${student._id}`}>Ver Historial de Pago</Tooltip>}
                      >
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="rounded-pill px-3 font-weight-bold"
                          onClick={() => openPaymentModal(student._id)}
                        >
                          <FontAwesomeIcon icon={faCircleDollarToSlot} className="mr-1" />
                          Pagos
                        </Button>
                      </OverlayTrigger>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      {/* Floating scroll to top */}
      {Array.isArray(studentsList) && studentsList.length > SCROLL_TO_TOP_THRESHOLD && (
        <Button
          variant="primary"
          onClick={scrollToTop}
          className="shadow-lg rounded-circle d-flex align-items-center justify-content-center"
          style={{
            position: "fixed",
            bottom: "25px",
            right: "25px",
            width: "48px",
            height: "48px",
            zIndex: 1050,
          }}
        >
          <FontAwesomeIcon icon={faArrowUp} />
        </Button>
      )}

      {/* Hidden textarea fallback for legacy copy */}
      <textarea
        ref={inputRef}
        value={copyValue}
        readOnly
        style={{ position: "absolute", left: "-9999px" }}
      />

      <PaymentHistorial
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        studentId={selectedStudentId}
      />

      <ExportCsvModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        currentQuery={currentQuery}
        visibleStudents={studentsList}
        totalRecords={studentCount}
      />
    </Col>
  );
}
